import { Component, type ReactElement, type ReactNode } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

interface CustomInputProps {
    localStorageKey: string;
    containerClass?: string;
    childClass?: string;
    disableDraggableFocus?: boolean;
    children: ReactNode[];
}
interface CustomInputState {
    children: ReactElement[];
}

class DndHorizontalList extends Component<CustomInputProps, CustomInputState> {
    constructor(props: CustomInputProps) {
        super(props);
        this.state = {
            children: props.children as ReactElement[],
        };
    }

    render(): ReactElement {
        return (
            <DragDropContext onDragEnd={this.onDragEnd}>
                <Droppable droppableId="droppable" direction="horizontal">
                    {(provided) => (
                        <div className={this.props.containerClass} ref={provided.innerRef} style={this.getListStyle()} {...provided.droppableProps}>
                            {this.state.children.sort(this.sortByArrayFromLocalStorage).map((item, index) => (
                                <Draggable
                                    key={item.key}
                                    draggableId={item.key.toString()}
                                    index={index}
                                    isDragDisabled={this.state.children.length <= 1}
                                >
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            tabIndex={this.props.disableDraggableFocus ? -1 : provided.dragHandleProps.tabIndex}
                                            style={{
                                                ...provided.draggableProps.style,
                                                ...this.getItemStyle(provided.draggableProps.style, snapshot),
                                            }}
                                            className={this.props.childClass}
                                        >
                                            {item}
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        );
    }

    // a little function to help us with reordering the result
    private reorder = (list: ReactElement[], startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    private getItemStyle = (draggableStyle, snapshot) => ({
        transform: (draggableStyle.transform || '') + (snapshot.isDragging ? 'scale(1.02)' : ''),
    });

    private getListStyle = () => ({
        display: 'flex',
        padding: 8,
        overflow: 'auto hidden',
        width: '100%',
    });

    private onDragEnd = (result): void => {
        if (!result.destination) {
            return;
        }

        const children = this.reorder(this.state.children, result.source.index, result.destination.index);

        localStorage.setItem(this.props.localStorageKey, JSON.stringify(children.map((x) => x.key)));

        this.setState({ children });
    };

    private sortByArrayFromLocalStorage = (a, b): number => {
        const arrayFromLocalStorage = JSON.parse(localStorage.getItem(this.props.localStorageKey));
        const sortingArray = arrayFromLocalStorage ? arrayFromLocalStorage : [];

        return sortingArray.indexOf(a.key) - sortingArray.indexOf(b.key);
    };
}

export { DndHorizontalList };
